import { TimeZoneType } from "../../constants/timezones"
import { HolidayViewModel } from "../../holidays/api/holiday"
import { TreeGridTranslations } from "../hooks/use-translations"
import { Option, TreeGridColumn } from "../types"
import { makeColorKeysEnum } from "../utils"
import { makeColumns } from "./columns"
import { makeCfgBase } from "../base/layout/make-cfg-base"
import { makeColumnLayout } from "../base/layout/make-column-layout"
import { ToolbarBuilder } from "../base/layout/make-toolbar"

export const makeLayout = (props: MakeLayoutProps) => {
  const Cfg = makeCfg(props)
  const Cols = makeColumns(props)
  const Head = makeHead(props)
  const Header = makeHeader({ columns: Cols, translations: props.translations })
  const Lang = makeLang(props)
  const Toolbar = makeToolbar(props)
  const Actions = makeActions()
  const Solid = [{ id: "NoData", Html: props.translations.noData }]
  const columnLayout = makeColumnLayout(Cols)

  return {
    Cfg,
    ...columnLayout,
    Head,
    Header,
    Toolbar,
    Lang,
    Actions,
    Solid,
  }
}

const makeCfg = ({ id }: { id: string }) => {
  const baseCfg = makeCfgBase({ id })
  return {
    ...baseCfg,
    Sort: "plannedEndDate,plannedStartDate",
    MainCol: "open",
    NoTreeLines: 1,
    Version: 5,
  }
}

const makeHead = ({ dateFormat, gridInfo, isFilterRowVisible, translations }: MakeHeadProps) => {
  const headerRow = {
    id: "headerRow",
    Name: translations.pageHeaderRowLabel,
    Space: -1,
    Visible: 0,
    PanelVisible: 0,
    CanExport: 0,
    CanPrint: 2,
    Cells: `Logo,GridInfo,PrintDate`,
    LogoRelWidth: 1,
    LogoAligh: "Left",
    LogoVAligh: "middle",
    LogoType: "Html",
    Logo: `<img style="height: 40px;" src="/favicon.png"  alt="company logo"/>`,
    PrintDate: new Date(),
    PrintDateType: "Date",
    PrintDateFormat: dateFormat,
    PrintDateCanEdit: 0,
    GridInfo: `<span class="largeText">${gridInfo}<span>`,
    GridInfoType: "Html",
    GridInfoRelWidth: 2,
  }
  const filtersRow = {
    CanExport: 0,
    CanPrint: 0,
    id: "filterRow",
    Kind: "Filter",
    ganttBarColorRange: 1,
    ganttBarColorFilterEnumKeys: 1,
    ganttBarColorEnumKeys: makeColorKeysEnum(translations),
    statusFilterOff: translations.statusFilterOffLabel,
    responsibleFilterOff: translations.responsibleFilterOffLabel,
    participantsFilterOff: translations.participantsFilterOffLabel,
    suppliersFilterOff: translations.suppliersFilterOffLabel,
    workspacesFilterOff: translations.workspacesFilterOffLabel,
    stateFilterOff: translations.stateFilterOffLabel,
    customersFilterOff: translations.customersFilterOffLabel,
    Visible: isFilterRowVisible ? 1 : 0,
  }

  return [headerRow, filtersRow]
}

const makeHeader = ({ columns, translations }: MakeHeaderProps) => {
  const headerColumns = [...columns.LeftCols, ...columns.Cols, ...columns.RightCols]
  const Header: Record<string, string | number> = {
    Name: translations.headerRowLabel,
    plannedStartDateClass: "text-right",
    plannedEndDateClass: "text-right",
    actualStartDateClass: "text-right",
    actualEndDateClass: "text-right",
    statusClass: "text-center",
    ganttBarColorClass: "text-center",
    Wrap: 1,
  }
  headerColumns.forEach((col) => {
    if (col.Name) {
      Header[col.Name] = col.header as string
    }
  })

  Header.Panel = " "
  return Header
}

const makeToolbar = ({
  canCreateProjects,
  isFilterRowVisible,
  showGanttToolbarActions,
  translations,
}: MakeToolbarProps) => {
  /**
   * @link https://www.treegrid.com/Doc/GlobalSettings.htm#DefaultToolbar
   */
  return new ToolbarBuilder()
    .addCustomToolbarConfig({
      Cells10Search: "AddProject,Search,Summary",
      AddProjectType: "Button",
      AddProjectButton: "Button",
      AddProject: canCreateProjects
        ? `<button data-test="add-project-button">${translations.addProjectButtonLabel}<span class="icon"> + </span></button>`
        : 0,
      AddProjectClass: "toolbarCustomButton toolbarCustomButton--primary",
    })
    .addToolbarSearch(translations)
    .addToolbarSummary(translations.toolbarActivitiesListSummaryText)
    .addToolbarFilter(isFilterRowVisible)
    .addToolbarGanttZoom({ showGanttToolbarActions, translations })
    .addToolbarTips(translations)
    .build()
}

const makeLang = ({ firstDayOfWeek, dateSeparator, translations }: MakeLangProps) => {
  return {
    Format: {
      FirstWeekDay: firstDayOfWeek,
      GMT: 1,
      DateSeparator: dateSeparator,
      LongDayNames: translations.longDayNames,
      ShortDayNames: translations.shortDayNames,
      Day2CharNames: translations.day2CharNames,
      Day1CharNames: translations.day1CharNames,
      LongMonthNames: translations.longMonthNames,
      LongMonthNames2: translations.longMonthNames,
      ShortMonthNames: translations.shortMonthNames,
    },
    MenuColumns: {
      Caption: translations.chooseItemsLabel,
      ColsCaption: translations.chooseColumnsLabel,
      SpaceCaption: translations.chooseToolbarsLabel,
      RowsCaption: translations.chooseRowsLabel,
    },
    MenuButtons: {
      Ok: translations.okButtonLabel,
      Cancel: translations.cancelButtonLabel,
      HideAll: translations.hideAllButtonLabel,
      All: translations.allOnButtonLabel,
      Clear: translations.clear,
    },
    MenuPrint: {
      Caption: translations.printOptionsLabel,
      PrintOptions: translations.printOptionsLabel,
      PrintRows: translations.printMaxRowsPerPageLabel,
      PrintPageRoot: translations.printStartRowLabel,
      PrintPageSize: translations.printPageSizeLabel,
      PrintPageOptions: translations.printBrowserPrintInfoLabel,
      PrintPageOrientation: translations.printPageOrientationLabel,
      PrintPageOrientations: `${translations.portraitLabel},${translations.landscapeLabel}`,
      PrintMarginWidth: translations.printMarginWidthLabel,
      PrintMarginHeight: translations.printMarginHeightLabel,
      PDFFitPage: translations.printFitToPageLabel,
      PDFFitPages: `${translations.noneLabel},${translations.widthLabel},${translations.heightLabel},${translations.singlePageLabel}`,
      PrintDPI: translations.printDPIRatioLabel,
      PrintWidth: translations.printPageWidthLabel,
      PrintHeight: translations.printPageHeightLabel,
      PrintResults: translations.printSizeInfo,
    },
    MenuExport: {
      Caption: translations.exportOptionsLabel,
      ExportOptions: translations.exportOptionsLabel,
      ExportName: translations.exportFileNameLabel,
      ExportFormat: translations.exportFileFormatLabel,
      ExportExpanded: translations.exportAllRowsExpandedLabel,
      ExportOutline: translations.exportTreeInExcelLabel,
      ExportIndent: translations.exportIndentTreeInMainColumnLabel,
      ExportFixedRows: translations.exportFreezeHeadRowsLabel,
      ExportFixedCols: translations.exportFreezeLeftColumnsLabel,
      ExportEmpty: translations.exportEmptyCellsLabel,
    },
    MenuFilter: {
      F0: translations.filterOffLabel,
      F1: translations.filterEqualLabel,
      F2: translations.filterNotEqualLabel,
      F3: translations.filterLessThanLabel,
      F4: translations.filterLessThanOrEqualLabel,
      F5: translations.filterGreaterThanLabel,
      F6: translations.filterGreaterThanOrEqualLabel,
      F7: translations.filterBeginsWithLabel,
      F8: translations.filterDoesnotBeginWithLabel,
      F9: translations.filterEndsWithLabel,
      F10: translations.filterDoesnotEndWithLabel,
      F11: translations.filterContainsLabel,
      F12: translations.filterDoesnotContainLabel,
    },
    Gantt: {
      ErrEnd: translations.startDateAfterEndDateError,
      ErrStart: translations.startDateAfterEndDateError,
    },
  }
}

const makeActions = () => {
  return {
    OnIns: "", // This is for resetting the default behavior of treegrid, which is to AddRow on insert key press
    OnDel: "", // This is for resetting the default behavior of treegrid, which is to DeleteRow on delete key press
  }
}

type MakeHeaderProps = {
  columns: MadeColumns
  translations: TreeGridTranslations
}

type MadeColumns = {
  LeftCols: TreeGridColumn[]
  Cols: TreeGridColumn[]
  RightCols: TreeGridColumn[]
}

type MakeLayoutProps = {
  id: string
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
  isFilterRowVisible: boolean
  translations: TreeGridTranslations
  canCreateProjects: boolean
  options: Option[]
  showGantt: boolean
  showGanttToolbarActions: boolean
  timeZone: TimeZoneType
  weekendDays: DayOfWeek[]
  holidays: HolidayViewModel[]
  ganttZoomLeftEdgeRefDate: Date
  ganttZoomRightEdgeRefDate: Date
}

type MakeToolbarProps = {
  canCreateProjects: boolean
  isFilterRowVisible: boolean
  showGanttToolbarActions: boolean
  translations: TreeGridTranslations
}

type MakeHeadProps = {
  isFilterRowVisible: boolean
  dateFormat: string
  gridInfo: string
  translations: TreeGridTranslations
}

type MakeLangProps = {
  firstDayOfWeek: DayOfWeek
  dateSeparator: string
  translations: TreeGridTranslations
}
